import Immutable from 'immutable';

/**
 * Reason a coupon cannot be entered for the current cart, or null when it can.
 *
 * - 'free': the single product is free — a coupon is meaningless.
 * - 'multi': more than one product line, or a quantity above 1.
 * - 'someone_else': the order is placed for another person.
 */
export type BlockReason = 'free' | 'multi' | 'someone_else' | null;

export interface CouponEligibility {
  /** Number of distinct product lines in the cart. */
  lineCount: number;
  /** Quantity of the single line (0 when there is not exactly one line). */
  quantity: number;
  /** Whether the single product is free. */
  isFree: boolean;
  /** Whether a coupon may be entered. */
  isEligible: boolean;
  /** Why the coupon is blocked, or null when eligible. */
  blockReason: BlockReason;
}

/**
 * Derives coupon eligibility from the cart contents.
 *
 * A coupon is only valid for a single product line at quantity 1, when that
 * product is not free and the order is not placed for someone else.
 *
 * @param products - The Immutable list of cart products.
 * @param forSomeoneElse - Whether the order is for another person.
 *
 * @returns The eligibility breakdown for the current cart.
 */
export function getCouponEligibility(
  products: Immutable.List<any>,
  forSomeoneElse: boolean,
): CouponEligibility {
  const lineCount = products.size;
  const quantity =
    lineCount === 1
      ? parseInt(String(products.getIn([0, 'attributes', 'quantity'], 1)), 10)
      : 0;
  const price =
    lineCount === 1
      ? parseFloat(
        String(products.getIn([0, 'attributes', 'unit_price', 'number'], '0')),
      )
      : 0;

  const isFree = lineCount === 1 && price === 0;
  const isEligible =
    lineCount === 1 && quantity === 1 && !isFree && !forSomeoneElse;

  let blockReason: BlockReason = null;
  if (!isEligible) {
    if (isFree) {
      blockReason = 'free';
    } else if (lineCount > 1 || quantity > 1) {
      blockReason = 'multi';
    } else if (forSomeoneElse) {
      blockReason = 'someone_else';
    }
  }

  return {
    lineCount,
    quantity,
    isFree,
    isEligible,
    blockReason,
  };
}

/**
 * Returns the label of the coupon currently applied to the cart, or null when
 * none is applied.
 *
 * Read from the order-item promotion adjustments (is_coupon) — the only coupon
 * data present in the cart payload after a page reload. The entered code itself
 * is not part of that payload, so the promotion label stands in for it when the
 * applied state is restored on load.
 *
 * @param products - The Immutable list of cart products.
 *
 * @returns The applied coupon's promotion label, or null when none is applied.
 */
export function getAppliedCouponLabel(
  products: Immutable.List<any>,
): string | null {
  let label: string | null = null;
  products.forEach((product: any) => {
    const adjustments = product.getIn(
      ['attributes', 'adjustments'],
      Immutable.List(),
    );
    adjustments.forEach((adjustment: any) => {
      if (
        adjustment.get('type') === 'promotion' &&
        adjustment.get('is_coupon')
      ) {
        label = adjustment.get('promotion_label') || label;
      }
    });
  });
  return label;
}

/**
 * Extracts the per-coupon thank-you message from the cart's promotion
 * adjustments, exposed by the backend on the adjustment's description.
 *
 * @param products - The Immutable list of cart products.
 *
 * @returns The promotion description, or null when none is set.
 */
export function getCouponDescription(
  products: Immutable.List<any>,
): string | null {
  let description: string | null = null;
  products.forEach((product: any) => {
    const adjustments = product.getIn(
      ['attributes', 'adjustments'],
      Immutable.List(),
    );
    adjustments.forEach((adjustment: any) => {
      if (
        adjustment.get('type') === 'promotion' &&
        adjustment.get('description')
      ) {
        description = adjustment.get('description');
      }
    });
  });
  return description;
}
